/**
 * BusinessSetupContext — Phase 2D Business Activation Workflow.
 *
 * Single source of truth for the data-driven 9-step business activation
 * (see backend services/business_activation.py). Distinct from the
 * auth-focused ActivationContext (/auth/me).
 *
 * - Fetches GET /api/onboarding/activation on mount + route change + on
 *   `activation:saved` / `onboarding-step-complete` window events.
 * - Runs the SAVE-TO-NEXT coach: when a step flips pending→complete, a toast
 *   prompts the user to continue to the next incomplete step (with a button
 *   that routes there). Celebrates full completion once.
 * - Exposes markReviewed(step) for the two human-review steps
 *   (public_profile, communication).
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User, Palette, Wrench, MapPin, Shield, MessageSquare,
  CreditCard, Globe, CalendarPlus,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STEP_ICONS = {
  business_identity: User,
  branding: Palette,
  services: Wrench,
  service_area: MapPin,
  verification: Shield,
  communication: MessageSquare,
  payments: CreditCard,
  public_profile: Globe,
  first_job: CalendarPlus,
};

const BusinessSetupContext = createContext({
  loading: true,
  setup: null,
  setupInProgress: false,
  refresh: () => {},
  markReviewed: async () => {},
});

export const useBusinessSetup = () => useContext(BusinessSetupContext);

const _token = () => {
  try {
    return (
      localStorage.getItem('access_token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token') ||
      null
    );
  } catch {
    return null;
  }
};

export const BusinessSetupProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null);
  const prevRef = useRef(null);
  const lastFetch = useRef(0);

  const applyCoach = useCallback((next, prev) => {
    if (!prev || !next || !Array.isArray(next.steps)) return;
    // Demo Mode owns the screen — never fire setup-coach toasts over the demo.
    if (typeof window !== 'undefined' && localStorage.getItem('demo_mode') === 'provider') return;
    const prevById = Object.fromEntries(prev.steps.map((s) => [s.id, s.status]));

    // Full completion — celebrate once.
    if (next.all_complete && prev.steps.some((s) => s.status === 'pending')) {
      toast.success('Your business is operational! 🎉', {
        description: 'Every required setup step is complete.',
        duration: 9000,
      });
      return;
    }

    // A step just flipped pending → complete: prompt + route to next.
    const newlyDone = next.steps.find(
      (s) => s.status === 'complete' && prevById[s.id] === 'pending',
    );
    if (newlyDone) {
      const nextStep = next.steps.find((s) => s.id === next.first_incomplete);
      toast.success(`${newlyDone.label} complete`, {
        description: nextStep ? `Next up: ${nextStep.label}` : 'Almost done!',
        action: nextStep
          ? { label: 'Continue', onClick: () => navigate(nextStep.route) }
          : undefined,
        duration: 9000,
      });
    }
  }, [navigate]);

  const fetchSetup = useCallback(async () => {
    const token = _token();
    if (!token) {
      setSetup(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/onboarding/activation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      applyCoach(res.data, prevRef.current);
      prevRef.current = res.data;
      setSetup(res.data);
      lastFetch.current = Date.now();
    } catch (err) {
      // Super-admin / no-company / network blip → leave last known state.
    } finally {
      setLoading(false);
    }
  }, [applyCoach]);

  // Mount + route change (lightly throttled).
  useEffect(() => {
    if (Date.now() - lastFetch.current < 1200) return;
    fetchSetup();
  }, [fetchSetup, location.pathname]);

  // Re-fetch after any setup save.
  useEffect(() => {
    const onSaved = () => { setTimeout(fetchSetup, 500); };
    window.addEventListener('activation:saved', onSaved);
    window.addEventListener('onboarding-step-complete', onSaved);
    return () => {
      window.removeEventListener('activation:saved', onSaved);
      window.removeEventListener('onboarding-step-complete', onSaved);
    };
  }, [fetchSetup]);

  const markReviewed = useCallback(async (step) => {
    const token = _token();
    if (!token) return null;
    try {
      const res = await axios.post(
        `${API}/onboarding/activation/mark-reviewed`,
        { step },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      applyCoach(res.data, prevRef.current);
      prevRef.current = res.data;
      setSetup(res.data);
      return res.data;
    } catch (err) {
      return null;
    }
  }, [applyCoach]);

  const value = {
    loading,
    setup,
    setupInProgress: !!setup && !setup.all_complete,
    refresh: fetchSetup,
    markReviewed,
  };

  return (
    <BusinessSetupContext.Provider value={value}>
      {children}
    </BusinessSetupContext.Provider>
  );
};

export default BusinessSetupProvider;
